<template>
  <div
    id="info_hero"
    class="vi-hero"
    :style="item.pic_hero_parsed ? 'background-image:url(' + item.pic_hero_parsed + ')' : ''"
  >
    <!-- Cinematic blurred backdrop (same pattern as videoPage) -->
    <div
      v-if="item.pic_hero_parsed"
      class="vi-hero__backdrop"
      :style="{ backgroundImage: 'url(' + item.pic_hero_parsed + ')' }"
      aria-hidden="true"
    ></div>

    <!-- Gradient layers -->
    <div class="vi-hero__grad vi-hero__grad--bottom" aria-hidden="true"></div>
    <div class="vi-hero__grad vi-hero__grad--side"   aria-hidden="true"></div>
    <div class="vi-hero__grad vi-hero__grad--top"    aria-hidden="true"></div>

    <!-- Hero content: type pill / meta chips / title / synopsis / CTAs.
         Rendered for non-subscribers too — the play CTA is how they start the
         free preview. The block itself lives on the player page. -->
    <div class="vi-hero__content">

      <!-- Meta row: accent type pill + meta chips -->
      <div class="vi-hero__meta" v-if="typeLabel || heroYear || heroRating || heroRuntime">
        <span class="vp-meta__pill vp-pill--accent">{{ typeLabel }}</span>
        <span v-if="heroYear"    class="vp-meta__chip">{{ heroYear }}</span>
        <span v-if="heroRating"  class="vp-meta__chip">{{ heroRating }}</span>
        <span v-if="heroRuntime" class="vp-meta__chip">{{ heroRuntime }}</span>
        <span v-if="categoryName" class="vp-meta__chip">{{ categoryName }}</span>
      </div>

      <h1 class="vi-hero__title" v-if="heroTitle">{{ heroTitle }}</h1>

      <p class="vi-hero__synopsis" v-if="heroSynopsis">{{ heroSynopsis }}</p>

      <div class="vi-hero__ctas">
        <router-link :to="'/item/' + item.id" class="vi-hero__play-link" aria-label="הפעל סרטון">
          <button type="button" class="vi-cta vi-cta--primary">
             הפעל
             <svg width="20" height="20" style="rotate:180deg;" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
          </button>
        </router-link>

        <button type="button" class="vi-cta vi-cta--secondary" @click="$emit('addToList')">
          הרשימה שלי 
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
         
        </button>

      </div>
    </div>

    <slot name="paywall"></slot>
  </div>
</template>

<script>
export default {
  name: 'VideoInfoHero',
  props: {
    item:           { type: Object,  default: () => ({}) },
    isUserCanWatch: { type: Boolean, default: false },
    title:          { type: String,  default: '' },
    year:           { type: [String, Number], default: '' },
    rating:         { type: String,  default: '' },
    runtime:        { type: String,  default: '' },
    synopsis:       { type: String,  default: '' },
  },
  computed: {
    typeLabel() {
      if (this.item.type === 'live') return 'שידור חי';
      if (this.item.type === 'series') return 'סדרה';
      return 'סרט';
    },
    categoryName() {
      return (this.item.main_category && this.item.main_category.name) ? this.item.main_category.name : '';
    },
    heroTitle() {
      return this.title || this.item.title || '';
    },
    heroYear() {
      if (this.year) return String(this.year);
      return this.item.year ? String(this.item.year) : '';
    },
    heroRating() {
      return this.rating || this.item.rating || '';
    },
    heroRuntime() {
      if (this.runtime) return this.runtime;
      if (this.item.duration_formatted) return this.item.duration_formatted;
      if (this.item.duration && Number(this.item.duration) > 0) {
        const mins = Math.round(Number(this.item.duration) / 60);
        return mins + ' דקות';
      }
      return '';
    },
    heroSynopsis() {
      return this.synopsis || this.item.description || this.item.full_description || '';
    },
  },
};
</script>

<style scoped lang="scss">
.vi-hero {
  position: relative;
  background-size: cover;
  background-position: center top;
  height: 75vh;
  min-height: 580px;
  overflow: hidden;
}

/* When paywall is inside, allow the hero to grow and stop clipping it */
.vi-hero:has(#unactive_screen) {
  height: auto;
  min-height: 0;
  overflow: visible;
}

/* ── Cinematic blurred backdrop ── */
.vi-hero__backdrop {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center top;
  filter: blur(10px) brightness(0.45) saturate(1.1);
  transform: scale(1.1);
  z-index: 0;
  pointer-events: none;
}

/* ── Gradient layers ── */
.vi-hero__grad {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

/* Hide gradients entirely when paywall is shown — they cause the dark overlay */
.vi-hero:has(#unactive_screen) .vi-hero__grad {
  display: none;
}

.vi-hero__grad--bottom {
  background: linear-gradient(180deg, transparent 25%, rgba(0, 0, 0, 0.92) 100%);
}

.vi-hero__grad--side {
  background: linear-gradient(to left, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.3) 45%, transparent 70%);
}

[dir="rtl"] .vi-hero__grad--side {
  background: linear-gradient(to right, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.3) 45%, transparent 70%);
}

.vi-hero__grad--top {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, transparent 35%);
}

/* ── Hero content block ── */
.vi-hero__content {
  position: absolute;
  bottom: 12%;
  inset-inline-start: 60px;
  max-width: 620px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 18px;
  
}

/* ── Meta row: chip pills ── */
.vi-hero__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Reuse videoPage chip/pill classes — defined here since hero is isolated */
.vp-meta__pill {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: var(--color-text);
  padding: 3px 10px;
  border-radius: 4px;
  font-family: var(--font-hebrew);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.vp-pill--accent {
  background: linear-gradient(135deg, var(--color-accent), var(--color-accentHover, var(--color-accent)));
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 8px rgba(225, 29, 42, 0.4);
}

.vp-meta__chip {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-hebrew);
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  padding: 2px 10px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
}

/* ── Title ── */
.vi-hero__title {
  font-family: var(--font-hebrew);
  font-size: 58px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1px;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.75);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Synopsis ── */
.vi-hero__synopsis {
  font-family: var(--font-hebrew);
  font-size: 17px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  max-width: 640px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── CTAs ── */
.vi-hero__ctas {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.vi-hero__play-link {
  text-decoration: none;
}

/* Inline CTA buttons — match vp-action-btn from videoPage */
.vi-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 52px;
  padding-inline: var(--space-8);
  border-radius: var(--radius-sm);
  font-family: var(--font-hebrew);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: background 150ms ease, transform 150ms ease, box-shadow 150ms ease;
  white-space: nowrap;
}

.vi-cta--primary {
  background: var(--color-accent);
  color: #fff;
  box-shadow: 0 4px 20px rgba(225, 29, 42, 0.5);
}

.vi-cta--primary:hover {
  background: var(--color-accentHover);
  box-shadow: 0 6px 28px rgba(225, 29, 42, 0.65);
  transform: translateY(-1px);
}

.vi-cta--primary:active {
  background: var(--color-accentPress);
  transform: translateY(0);
}

.vi-cta--secondary {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.vi-cta--secondary:hover {
  background: rgba(255, 255, 255, 0.22);
}

.vi-cta:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .vi-hero {
    height: 60vh;
    min-height: 440px;
  }

  .vi-hero__content {
    inset-inline-end: var(--space-5);
    inset-inline-start: var(--space-5);
    max-width: 100%;
    margin:20px 20px 0;
    bottom: 8%;
    gap: 12px;
  }

  .vi-hero__title {
    font-size: 34px;
    letter-spacing: -0.5px;
  }

  .vi-hero__synopsis {
    font-size: 15px;
    -webkit-line-clamp: 3;
  }

  .vi-cta {
    height: 44px;
    font-size: 14px;
  }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .vi-hero__backdrop,
  .vi-cta { transition: none; }
}
</style>
